/* OCR-ALUMNO (PR #347, §C) — LAS FOTOS DEL ALUMNO: helpers puros del lado consumidor.

   Aquí vive lo que no necesita DOM ni React: qué archivo cuenta como foto, el nombre
   prellenado del paquete (formato de §D, con el idioma de la app y la hora local del
   dispositivo), el orden numerado y la reducción en el navegador antes de mandar.
   Nada de esto lo importa el carril del profesor (candado piloto1.superficies). */

export const MAX_FOTOS = 100;
export const LADO_MAX_FOTO = 1600;
export const JPEG_CALIDAD = 0.85;

const EXTENSIONES_FOTO = ['.jpg', '.jpeg', '.png', '.heic', '.heif'];
const TIPOS_FOTO = ['image/jpeg', 'image/png', 'image/heic', 'image/heif'];

/* `accept` del selector: lo de siempre más las fotos (JPG, PNG, HEIC/HEIF). */
export const ACCEPT_SUBIDA = '.pdf,.docx,.pptx,.txt,image/jpeg,image/png,image/heic,image/heif,.jpg,.jpeg,.png,.heic,.heif';

export function extensionDe(nombre) {
  const n = String(nombre || '').toLowerCase();
  const i = n.lastIndexOf('.');
  return i >= 0 ? n.slice(i) : '';
}

/* Una foto es lo que el navegador etiqueta como imagen de las tres familias, o lo que
   trae su extensión (Safari/iOS a veces manda HEIC sin `type`). */
export function esFoto(file) {
  if (!file) return false;
  const tipo = String(file.type || '').toLowerCase();
  if (TIPOS_FOTO.includes(tipo)) return true;
  return EXTENSIONES_FOTO.includes(extensionDe(file.name));
}

export function esHeic(file) {
  const tipo = String(file?.type || '').toLowerCase();
  return tipo === 'image/heic' || tipo === 'image/heif' || ['.heic', '.heif'].includes(extensionDe(file?.name));
}

/* El nombre prellenado del paquete (§D):
     EN  "Photos, Sep 6, 2026, 2:32 PM"
     ES  "Fotos, 6 sep 2026, 14:32"   (t7-3 remate R2: coma, no punto medio)   (sin punto en "sep"; si el motor lo pone, se quita)
   `prefijo` es la palabra traducida ("Photos" / "Fotos"); `fecha` es un Date (hora local). */
export function nombrePrellenado(lang, prefijo, fecha = new Date()) {
  const es = String(lang || 'en').toLowerCase().startsWith('es');
  let cuerpo;
  if (es) {
    const dia = new Intl.DateTimeFormat('es-MX', { day: 'numeric', month: 'short', year: 'numeric' })
      .format(fecha).replace(/\./g, '');
    const hora = new Intl.DateTimeFormat('es-MX', { hour: '2-digit', minute: '2-digit', hour12: false })
      .format(fecha);
    cuerpo = `${dia}, ${hora}`;
  } else {
    cuerpo = new Intl.DateTimeFormat('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
    }).format(fecha);
  }
  return `${prefijo}, ${cuerpo}`;
}

/* El orden numerado: `orden` es la lista de ids incluidos, en el orden en que se van a
   mandar. Tocar una foto la saca (las demás se renumeran solas); tocarla otra vez la
   vuelve a meter AL FINAL, con el siguiente número. */
export function alternarEnOrden(orden, id) {
  return orden.includes(id) ? orden.filter((x) => x !== id) : [...orden, id];
}

export function numeroDe(orden, id) {
  const i = orden.indexOf(id);
  return i >= 0 ? i + 1 : null;
}

/* Reduce una foto en el navegador antes de mandarla: lado mayor ≤ 1,600, JPEG 0.85,
   enderezada por EXIF (`imageOrientation: "from-image"`). Si el navegador no decodifica
   (HEIC en Chrome, o un entorno sin canvas) manda el ORIGINAL y el servidor la abre. */
export async function redimensionarFoto(file) {
  if (typeof createImageBitmap !== 'function' || typeof document === 'undefined') return file;
  let bitmap;
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch {
    return file;
  }
  try {
    const lado = Math.max(bitmap.width, bitmap.height);
    const esc = lado > LADO_MAX_FOTO ? LADO_MAX_FOTO / lado : 1;
    const ancho = Math.max(1, Math.round(bitmap.width * esc));
    const alto = Math.max(1, Math.round(bitmap.height * esc));
    const canvas = document.createElement('canvas');
    canvas.width = ancho;
    canvas.height = alto;
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    ctx.drawImage(bitmap, 0, 0, ancho, alto);
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', JPEG_CALIDAD));
    if (!blob) return file;
    const nombre = String(file.name || 'foto').replace(/\.[^.]+$/, '') + '.jpg';
    return new File([blob], nombre, { type: 'image/jpeg' });
  } catch {
    return file;
  } finally {
    if (bitmap && typeof bitmap.close === 'function') bitmap.close();
  }
}
